import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { addNewData } from 'services/CommonServices';
import { alertOptions } from 'utils/Config';
import { MASTER_ENDPOINT } from 'utils/Constant';

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  candidateId?: string;
  onUploadSuccess?: () => void;
}

const modalStyle = {
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: 24,
  left: '50%',
  maxHeight: '90vh',
  maxWidth: '90%',
  outline: 'none',
  overflow: 'hidden',
  position: 'absolute' as const,
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
};

interface SelectedFile {
  file: File;
  id: string;
  name: string;
  size: number;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
  open,
  onClose,
  candidateId,
  onUploadSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB', alertOptions);
        return;
      }

      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.d长ocument',
        'image/png',
        'image/jpeg',
        'image/jpg',
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error(
          'Invalid file type. Please upload PDF, DOC, DOCX, PNG, or JPEG files.',
          alertOptions,
        );
        return;
      }

      setSelectedFile({
        file,
        id: Date.now().toString(),
        name: file.name,
        size: file.size,
      });
      setFileName(file.name);
    },
    [],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const file = event.dataTransfer.files?.[0];
      if (file) {
        const fakeEvent = {
          target: { files: [file] },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleFileSelect(fakeEvent);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    [],
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !candidateId) {
      toast.error('Please select a file', alertOptions);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile.file);
    formData.append('candidateId', candidateId);
    formData.append('type', 'candidateAttachment');
    if (fileName.trim()) {
      formData.append('fileName', fileName.trim());
    }

    try {
      const resp = await addNewData(
        formData,
        `${MASTER_ENDPOINT.Candidates}/attachments`,
        {},
        false,
      );

      if (resp) {
        toast.success('File uploaded successfully', alertOptions);
        handleRemoveFile();
        onUploadSuccess?.();
        onClose();
      }
    } catch (error: any) {
      toast.error(
        error?.message || 'Failed to upload file. Please try again.',
        alertOptions,
      );
    } finally {
      setUploading(false);
    }
  }, [
    selectedFile,
    candidateId,
    fileName,
    handleRemoveFile,
    onUploadSuccess,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    if (!uploading) {
      handleRemoveFile();
      onClose();
    }
  }, [uploading, handleRemoveFile, onClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="file-upload-modal-title"
      aria-describedby="file-upload-modal-description">
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '24px 23px',
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography
              id="file-upload-modal-title"
              sx={{
                color: '#242424',
                fontFamily: 'Red Hat Display SemiBold',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '22px',
              }}>
              Upload File
            </Typography>
            <Typography
              sx={{
                color: '#707070',
                fontFamily: 'Red Hat Display Regular',
                fontSize: '13px',
                lineHeight: '16px',
              }}>
              Select and upload the files from your device
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={uploading}
            sx={{
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
              color: '#6b7280',
              height: '32px',
              padding: '6px',
              width: '32px',
            }}>
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: '24px',
          }}>
          {/* File Upload Area */}
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              alignItems: 'center',
              backgroundColor: '#ffffff',
              border: '2px dashed #e0e0e0',
              borderRadius: '13.6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              height: '166px',
              justifyContent: 'center',
              padding: '24px',
              position: 'relative',
            }}>
            {selectedFile ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                }}>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}>
                  <Typography
                    sx={{
                      color: '#242424',
                      fontFamily: 'Red Hat Display Medium',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}>
                    {selectedFile.name}
                  </Typography>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '5px',
                    }}>
                    <Typography
                      sx={{
                        color: '#707070',
                        fontFamily: 'Red Hat Display Regular',
                        fontSize: '10px',
                        lineHeight: '14px',
                      }}>
                      {formatFileSize(selectedFile.size)} •
                    </Typography>
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: '5px',
                      }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: '#10b981',
                          borderRadius: '50%',
                          display: 'flex',
                          height: '12px',
                          justifyContent: 'center',
                          width: '12px',
                        }}>
                        <Box
                          sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: '50%',
                            height: '6px',
                            width: '6px',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: '#242424',
                          fontFamily: 'Red Hat Display Regular',
                          fontSize: '10px',
                          lineHeight: '14px',
                        }}>
                        Completed
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  onClick={handleRemoveFile}
                  disabled={uploading}
                  sx={{
                    color: '#707070',
                    fontFamily: 'Red Hat Display Regular',
                    fontSize: '10px',
                    lineHeight: '14px',
                    minWidth: 'auto',
                    padding: '4px 8px',
                    textTransform: 'none',
                  }}>
                  Remove
                </Button>
              </Box>
            ) : (
              <>
                <CloudUploadIcon
                  sx={{
                    color: '#616161',
                    fontSize: '32px',
                  }}
                />
                <Typography
                  component="label"
                  htmlFor="file-upload-input"
                  sx={{
                    color: '#242424',
                    cursor: 'pointer',
                    fontFamily: 'Red Hat Display Medium',
                    fontSize: '14px',
                    fontWeight: 500,
                    textDecoration: 'underline',
                  }}>
                  Click to upload or drag and drop
                </Typography>
                <input
                  ref={fileInputRef}
                  id="file-upload-input"
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                />
              </>
            )}
          </Box>

          {/* File Name Input */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <TextField
              fullWidth
              placeholder="Placeholder text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                sx: {
                  '& input::placeholder': {
                    color: '#707070',
                    opacity: 1,
                  },
                  borderRadius: '4px',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '13px',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#d1d1d1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#d1d1d1',
                  },
                  '&:hover fieldset': {
                    borderColor: '#d1d1d1',
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: '6px',
            justifyContent: 'flex-end',
            padding: '12px 24px',
          }}>
          <Button
            onClick={handleClose}
            disabled={uploading}
            sx={{
              '&:hover': {
                backgroundColor: '#f9fafb',
              },
              backgroundColor: '#ffffff',
              border: '1px solid #d1d1d1',
              borderRadius: '6px',
              color: '#242424',
              fontFamily: 'Red Hat Display SemiBold',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '20px',
              padding: '6px 12px',
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            sx={{
              '&:disabled': {
                backgroundColor: '#2d2d2d',
                color: '#ffffff',
                opacity: 0.6,
              },
              '&:hover': {
                backgroundColor: '#1a1a1a',
              },
              backgroundColor: '#2d2d2d',
              borderRadius: '6px',
              color: '#ffffff',
              fontFamily: 'Red Hat Display SemiBold',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '20px',
              padding: '6px 12px',
              textTransform: 'none',
            }}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
